import { AfterViewInit, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Fishbone Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-fishbone-timeline-graph',
  standalone: false,
  templateUrl: './fishbone-timeline-graph.component.html',
  styleUrl: './fishbone-timeline-graph.component.scss'
})
export class FishboneTimelineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private fishboneTimelineGraphReportSubscription: any;
  private isFishboneTimelineViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService
  ) {}

  ngOnInit(): void {
    // We need to run amCharts code outside of Angular's zone to prevent change detection issues
    this.zone.runOutsideAngular(() => {
      this.fishboneTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Fishbone Timeline Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isFishboneTimelineViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isFishboneTimelineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        if (document.getElementById(this.getChartId())) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Fishbone Timeline Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isFishboneTimelineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        const chartContainer = document.getElementById(this.getChartId());
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          setTimeout(() => {
            const retryContainer = document.getElementById(this.getChartId());
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  /** Unique chart container id (template uses this). */
  getChartId(): string {
    return 'chartdiv';
  }

  /** Category and value fields from base chart service. */
  private getChartFields(data: BarChartData[]): { categoryField: string; valueFields: string[] } {
    return {
      categoryField: this._baseChartService.getCategoryField(data),
      valueFields: this._baseChartService.getValueFields(data)
    };
  }

  createChart(data: BarChartData[]) {
    const chartId = this.getChartId();
    const chartContainer = document.getElementById(chartId);
    console.log('📦 Fishbone Timeline Chart container found:', !!chartContainer);
    
    if (!chartContainer) {
      console.error(`❌ Fishbone Timeline Chart container not found! ID: ${chartId}`);
      return;
    }

    try {
      // Dispose existing root if present
      if (this.root) {
        console.log('🧹 Disposing existing fishbone timeline chart root');
        this._baseChartService.disposeChart(this.root);
        this.root = null as any;
      }
      
      // Create new root
      this.root = this._baseChartService.createRoot(chartId);

      // Get chart fields
      const { categoryField, valueFields } = this.getChartFields(data);
      console.log('Fishbone Timeline Chart fields:', { categoryField, valueFields });

      // Set themes
      this.root.setThemes([
        am5themes_Animated.new(this.root)
      ]);

      // Transform data for fishbone timeline
      const timelineData = this.transformDataForFishbone(data, categoryField, valueFields);
      console.log('Fishbone Timeline Data:', timelineData);

      // Create XY Chart for fishbone visualization
      const chart = this.root.container.children.push(am5xy.XYChart.new(this.root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "none",
        paddingTop: 120,
        paddingBottom: 120,
        paddingLeft: 20,
        paddingRight: 20,
        layout: this.root.verticalLayout
      }));

      // Create X axis (value/position axis)
      let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(this.root, {
        min: 0,
        max: timelineData.length,
        strictMinMax: true,
        renderer: am5xy.AxisRendererX.new(this.root, {
          minGridDistance: 80
        })
      }));

      // Hide X axis labels and grid
      xAxis.get("renderer").labels.template.set("forceHidden", true);
      xAxis.get("renderer").grid.template.set("forceHidden", true);

      // Create Y axis (for vertical positioning)
      let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(this.root, {
        min: -100,
        max: 100,
        strictMinMax: true,
        renderer: am5xy.AxisRendererY.new(this.root, {})
      }));

      // Hide Y axis
      yAxis.get("renderer").labels.template.set("forceHidden", true);
      yAxis.get("renderer").grid.template.set("forceHidden", true);

      // Draw the main spine (horizontal line at y=0)
      let spineRange = yAxis.createAxisRange(yAxis.makeDataItem({ value: 0 }));
      spineRange.get("grid")?.setAll({
        stroke: am5.color(0x888888),
        strokeWidth: 2,
        strokeOpacity: 1,
        strokeDasharray: [3, 3],
        visible: true,
        forceHidden: false
      });

      // Create line series (invisible - just for placing bullets)
      let series = chart.series.push(am5xy.LineSeries.new(this.root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: "x",
        valueYField: "y"
      }));

      // Hide connecting lines
      series.strokes.template.set("strokeOpacity", 0);

      // Add fishbone bullets
      series.bullets.push((root: am5.Root, seriesObj: any, dataItem: any) => {
        const dataContext = dataItem.dataContext as any;
        const index = dataContext.index;
        const isAbove = index % 2 === 0;
        const color = chart.get("colors")?.getIndex(index % 10) || am5.color(0x3498db);
        const circleSize = dataContext.size || 25;

        // Create bullet container
        let bulletContainer = am5.Container.new(root, {
          centerX: am5.p50,
          centerY: am5.p50
        });

        // Calculate line angle and length
        const lineLength = 60;
        const angle = isAbove ? -60 : 60;
        const angleRad = (angle * Math.PI) / 180;
        const lineEndX = lineLength * Math.cos(angleRad);
        const lineEndY = lineLength * Math.sin(angleRad);

        // Create the dashed connecting line
        let lineGraphics = bulletContainer.children.push(am5.Graphics.new(root, {
          stroke: color,
          strokeWidth: 1,
          strokeOpacity: 0.7,
          strokeDasharray: [2, 2],
          draw: (display) => {
            display.moveTo(0, 0);
            display.lineTo(lineEndX, lineEndY);
          }
        }));

        // Create the main colored circle
        let circle = bulletContainer.children.push(am5.Circle.new(root, {
          radius: circleSize,
          fill: color,
          stroke: am5.color(0xffffff),
          strokeWidth: 2,
          strokeOpacity: 0.5,
          centerX: am5.p50,
          centerY: am5.p50,
          tooltipText: `${dataContext.text}: ${dataContext.value}`
        }));

        // Value label inside the circle (category + value only, no dates)
        bulletContainer.children.push(am5.Label.new(root, {
          text: String(dataContext.value ?? ''),
          fill: am5.color(0xffffff),
          fontSize: circleSize > 30 ? 12 : 10,
          fontWeight: "bold",
          centerX: am5.p50,
          centerY: am5.p50
        }));

        // Category text label at the end of the line
        let textLabel = bulletContainer.children.push(am5.Label.new(root, {
          text: dataContext.text,
          fill: color,
          fontSize: 10,
          fontWeight: "500",
          x: lineEndX + (isAbove ? -5 : -5),
          y: lineEndY + (isAbove ? -10 : 10),
          centerX: am5.p100,
          centerY: isAbove ? am5.p100 : am5.p0,
          rotation: angle
        }));

        return am5.Bullet.new(root, {
          sprite: bulletContainer
        });
      });

      // Prepare series data with x,y coordinates
      const seriesData = timelineData.map((item: any, idx: number) => ({
        ...item,
        x: idx + 0.5,
        y: 0 // All points on the spine
      }));

      // Set series data
      series.data.setAll(seriesData);

      const legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );
      legend.setAll({
        width: am5.percent(100),
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 8,
        marginBottom: 8
      });
      legend.markers.template.setAll({ width: 12, height: 12 });
      legend.labels.template.setAll({
        text: '{text}',
        fontSize: 12,
        oversizedBehavior: 'truncate',
        maxWidth: 180
      });
      legend.valueLabels.template.set('forceHidden', true);
      legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) {
          return fill;
        }
        const index = series.dataItems.indexOf(dataItem);
        return index >= 0 ? chart.get('colors')?.getIndex(index % 10) : fill;
      });
      legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) {
          return stroke;
        }
        const index = series.dataItems.indexOf(dataItem);
        return index >= 0 ? chart.get('colors')?.getIndex(index % 10) : stroke;
      });
      const updateLegend = () => {
        if (series.dataItems?.length > 0) {
          legend.data.setAll(series.dataItems);
        }
      };
      series.events.on('datavalidated', updateLegend);
      updateLegend();
      this._baseChartService.setupLegendAnimation(legend);

      // Trigger chart animation
      setTimeout(() => {
        chart.appear(1200, 100);
      }, 100);

      // Enable export menu
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'fishbone-timeline-chart',
        data: data,
      });

    } catch (error) {
      console.error('Error creating fishbone timeline chart:', error);
    }
  }

  /** Transform data for fishbone: category (label) and value (size) only, no dates. */
  private transformDataForFishbone(data: BarChartData[], categoryField: string, valueFields: string[]): any[] {
    const fishboneData: any[] = [];
    const valueField = valueFields[0] || 'value';

    data.forEach((item, itemIndex) => {
      const category = item[categoryField];
      const value = item[valueField];
      const numericValue = typeof value === 'number' ? value : (typeof value === 'string' ? parseFloat(value) : 0) || 0;
      const scaledSize = Math.max(15, Math.min(45, 15 + (Math.min(Math.abs(numericValue), 100) / 100) * 25));

      fishboneData.push({
        text: category !== undefined && category !== null ? String(category).trim() : `Item ${itemIndex + 1}`,
        size: scaledSize,
        value: value !== undefined && value !== null ? value : numericValue,
        valueField,
        index: itemIndex
      });
    });

    return fishboneData;
  }

  private updateChart(data: BarChartData[]) {
    if (this.isCreatingChart) return;
    const chartId = this.getChartId();
    if (!document.getElementById(chartId)) {
      setTimeout(() => this.updateChart(data), 100);
      return;
    }

    this.isCreatingChart = true;
    try {
      if (this.root) {
        this._baseChartService.disposeChart(this.root);
        this.root = undefined as any;
      }
      setTimeout(() => {
        try {
          this.createChart(data);
          BaseChartService.updateActiveChartData(data);
        } finally {
          this.isCreatingChart = false;
        }
      }, 200);
    } catch (e) {
      this.isCreatingChart = false;
    }
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.fishboneTimelineGraphReportSubscription) {
      this.fishboneTimelineGraphReportSubscription.unsubscribe();
    }
  }
}
